import { create } from 'zustand';
import { isNight } from '@boatnav/core';
import { posStore } from '../features/nav/posStore.js';

export type Theme = 'day' | 'night';
export type ThemeMode = 'auto' | 'day' | 'night';

const MODE_KEY = 'los.thememode';
const AUTO_INTERVAL_MS = 5 * 60_000;

function loadMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'auto' || saved === 'day' || saved === 'night') return saved;
    // Migrate the old day/night-only key.
    const legacy = localStorage.getItem('los.theme');
    if (legacy === 'day' || legacy === 'night') return legacy;
  } catch {
    /* private mode */
  }
  return 'day';
}

/** Effective theme for a mode: auto derives from civil twilight at the fix. */
function effectiveTheme(mode: ThemeMode): Theme {
  if (mode === 'day' || mode === 'night') return mode;
  const fix = posStore.getState().fix;
  if (!fix) return 'day'; // no position yet → default day
  return isNight(fix.lat, fix.lon, Date.now()) ? 'night' : 'day';
}

function apply(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'night' ? '#050607' : '#ece5d3');
}

interface ThemeState {
  mode: ThemeMode;
  theme: Theme;
  setMode: (m: ThemeMode) => void;
  /** Map quick-toggle: flip to the opposite explicit theme (manual override). */
  toggle: () => void;
  /** Re-derive the effective theme when in auto mode. */
  refreshAuto: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: loadMode(),
  theme: effectiveTheme(loadMode()),

  setMode: (mode) => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* ignore */
    }
    const theme = effectiveTheme(mode);
    apply(theme);
    set({ mode, theme });
  },

  toggle: () => {
    const next: Theme = get().theme === 'day' ? 'night' : 'day';
    get().setMode(next);
  },

  refreshAuto: () => {
    if (get().mode !== 'auto') return;
    const theme = effectiveTheme('auto');
    if (theme !== get().theme) {
      apply(theme);
      set({ theme });
    }
  },
}));

/** Apply the persisted theme before first paint, and arm auto re-evaluation. */
export function initTheme(): void {
  apply(useThemeStore.getState().theme);
  // Re-derive on new fixes and every 5 min while in auto mode (spec 07).
  posStore.subscribe((s, prev) => {
    if (s.fix !== prev.fix) useThemeStore.getState().refreshAuto();
  });
  setInterval(() => useThemeStore.getState().refreshAuto(), AUTO_INTERVAL_MS);
}
